import React from "react";
import { Chip, Container, Paper, Typography, makeStyles } from "@material-ui/core";
import NewReleasesOutlinedIcon from "@material-ui/icons/NewReleasesOutlined";
import RestoreOutlinedIcon from "@material-ui/icons/RestoreOutlined";
import TimerOutlinedIcon from "@material-ui/icons/TimerOutlined";
import ChatBubbleOutlineOutlinedIcon from "@material-ui/icons/ChatBubbleOutlineOutlined";
import ContactsOutlinedIcon from "@material-ui/icons/ContactsOutlined";
import HistoryOutlinedIcon from "@material-ui/icons/HistoryOutlined";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import NotificationsActiveOutlinedIcon from "@material-ui/icons/NotificationsActiveOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import ReplyAllOutlinedIcon from "@material-ui/icons/ReplyAllOutlined";

const replyUpdates = [
  {
    title: "Saiba na hora quando seu cliente responde",
    description: "Se você não estiver na conversa, aparece um aviso com som (e também no sistema, com a aba minimizada). Atendendo outro cliente, o aviso é discreto. Ele some ao clicar ou ao abrir a conversa e fica no sino por 24 horas. Só chegam avisos dos clientes que você atende.",
    icon: ReplyAllOutlinedIcon,
    featured: true,
  },
];

const updates = [
  {
    title: "Recupere os atendimentos após a queda",
    description: "Ao reconectar o WhatsApp, as mensagens disponíveis desde a queda entram nos atendimentos existentes ou criam novos, sem repetir mensagens.",
    icon: RestoreOutlinedIcon,
    featured: true,
  },
  {
    title: "Quem espera mais aparece primeiro",
    description: "No Dashboard e em Aguardando, cores e relógios mostram o tempo de espera. Verde no início, laranja na espera maior e vermelho quando precisa de atenção.",
    icon: TimerOutlinedIcon,
  },
  {
    title: "Comece uma conversa",
    description: "Use Mensagem direta, ao lado de Busca, para procurar pelo nome ou número e enviar uma mensagem. O atendimento é aberto para você continuar a conversa.",
    icon: ChatBubbleOutlineOutlinedIcon,
  },
  {
    title: "Contatos mais fáceis de reconhecer",
    description: "Encontre seus contatos pelo nome e veja suas fotos nas conversas, quando essas informações estiverem disponíveis no WhatsApp.",
    icon: ContactsOutlinedIcon,
  },
  {
    title: "Histórico na hora certa",
    description: "Ao receber uma mensagem ou abrir um atendimento, o sistema busca as conversas anteriores disponíveis daquele contato, sem carregar todas as conversas de uma vez.",
    icon: HistoryOutlinedIcon,
  },
  {
    title: "Saiba quem passou o atendimento",
    description: "Em Meu inbox, fica registrado quem transferiu um atendimento para você e quando isso aconteceu. Os registros continuam disponíveis para consultar depois.",
    icon: InboxOutlinedIcon,
  },
  {
    title: "Aviso vermelho no cabeçalho",
    description: "Se o WhatsApp desconectar, uma faixa vermelha avisa toda a equipe. O administrador tem um atalho para reconectar, e o aviso desaparece quando a conexão voltar.",
    icon: NotificationsActiveOutlinedIcon,
  },
  {
    title: "Dia e hora de cada mensagem",
    description: "Veja a data e o horário em que cada mensagem foi enviada, com horas, minutos e segundos, para acompanhar a conversa com clareza.",
    icon: EventOutlinedIcon,
  },
];

const releases = [
  { date: "2026-09-23", label: "23 de setembro de 2026", updates: replyUpdates },
  { date: "2026-09-10", label: "10 de setembro de 2026", updates },
];

const useStyles = makeStyles(theme => ({
  root: {
    maxWidth: 1120,
    paddingTop: 32,
    paddingBottom: 40,
    [theme.breakpoints.down("xs")]: { paddingTop: 24 },
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 28,
  },
  headerIcon: {
    display: "grid",
    placeItems: "center",
    width: 56,
    height: 56,
    flexShrink: 0,
    borderRadius: 18,
    color: theme.palette.type === "dark" ? "#6EE7B7" : theme.palette.primary.main,
    background: "rgba(16,185,129,.12)",
    "& svg": { fontSize: 30 },
    [theme.breakpoints.down("xs")]: { width: 46, height: 46, borderRadius: 14 },
  },
  section: { "& + &": { marginTop: 36 } },
  title: { fontSize: "1.75rem", fontWeight: 800, letterSpacing: "-.03em" },
  subtitle: { color: theme.palette.text.secondary, fontSize: ".9rem", marginTop: 4, lineHeight: 1.6 },
  release: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 18,
    paddingBottom: 18,
    borderBottom: `1px solid ${theme.custom.border}`,
  },
  badge: {
    fontWeight: 700,
    borderRadius: 8,
    ...(theme.palette.type === "dark" && { color: "#6EE7B7", borderColor: "#3C826C" }),
  },
  date: { color: theme.palette.text.secondary, fontSize: ".8rem" },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 16,
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  card: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    padding: 24,
    border: `1px solid ${theme.custom.border}`,
    borderRadius: 18,
    boxShadow: "none",
    [theme.breakpoints.down("xs")]: { padding: 18, gap: 12 },
  },
  featured: {
    borderColor: theme.palette.type === "dark" ? "rgba(52,211,153,.4)" : "#A7E2CD",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #16342D, #192A29)"
      : "linear-gradient(135deg, #EDFDF5, #F8FFFC)",
  },
  icon: {
    display: "grid",
    placeItems: "center",
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: 12,
    color: theme.palette.type === "dark" ? "#6EE7B7" : theme.palette.primary.main,
    background: "rgba(16,185,129,.09)",
    "& svg": { fontSize: 23 },
  },
  cardBody: { minWidth: 0 },
  cardTitle: { fontSize: ".98rem", fontWeight: 700, lineHeight: 1.5, marginBottom: 7 },
  description: { fontSize: ".86rem", lineHeight: 1.7, color: theme.palette.text.secondary },
}));

export default function Updates() {
  const classes = useStyles();

  return (
    <Container className={classes.root}>
      <header className={classes.header}>
        <div className={classes.headerIcon}><NewReleasesOutlinedIcon /></div>
        <div>
          <Typography component="h1" className={classes.title}>Atualizações</Typography>
          <Typography className={classes.subtitle}>Um resumo do que chegou para facilitar seus atendimentos.</Typography>
        </div>
      </header>
      {releases.map(release => (
        <section key={release.date} aria-label={"Novidades de " + release.label} className={classes.section}>
          <div className={classes.release}>
            <Chip label="Novidades" size="small" color="primary" variant="outlined" className={classes.badge} />
            <Typography component="time" dateTime={release.date} className={classes.date}>{release.label}</Typography>
          </div>
          <div className={classes.grid}>
            {release.updates.map(({ title, description, icon: Icon, featured }) => (
              <Paper component="article" key={title} className={`${classes.card} ${featured ? classes.featured : ""}`}>
                <div className={classes.icon}><Icon /></div>
                <div className={classes.cardBody}>
                  <Typography component="h2" className={classes.cardTitle}>{title}</Typography>
                  <Typography className={classes.description}>{description}</Typography>
                </div>
              </Paper>
            ))}
          </div>
        </section>
      ))}
    </Container>
  );
}
